import {
  useRef,
  useState,
  useMemo,
  useEffect,
  useCallback,
  RefObject,
} from 'react'
import { ThreeEvent } from '@react-three/fiber'
import { useShallow } from 'zustand/react/shallow'
import { TrackballControls as TrackballControlsImpl } from 'three-stdlib'

import { CameraControlsImpl } from '@react-three/drei'
import { CONTROLS, useCADPageStore } from '@/state'
import { useCADQuery } from '@/services/queries/cads'
import { useOperationSteps } from '@/services/queries/operation_steps'
import { Object3D, Object3DEventMap, Box3, Vector3 } from 'three'
import { getOperationStepBoundingBox } from '@/lib/cad/boundingBoxes/getOperationStepBoundingBox'
import { getTargetAssemblyGroupIds } from '@/lib/cad/operationSteps/getTargetAssemblyGroupIds'

const Y_AXIS = new Vector3(0, 1, 0)
const TOP_UP = new Vector3(0, 0, -1)
const BOTTOM_UP = new Vector3(0, 0, 1)

const getStableCameraUp = (direction: Vector3) => {
  const normalizedDirection = direction.clone().normalize()
  const preferredUp =
    Math.abs(normalizedDirection.dot(Y_AXIS)) > 0.98
      ? normalizedDirection.y > 0
        ? TOP_UP
        : BOTTOM_UP
      : Y_AXIS

  return preferredUp
    .clone()
    .sub(
      normalizedDirection
        .clone()
        .multiplyScalar(preferredUp.dot(normalizedDirection)),
    )
    .normalize()
}

interface useOrthographicViewsProps {
  cameraControlsRef: RefObject<CameraControlsImpl | null>
  trackballControlsRef: RefObject<TrackballControlsImpl | null>
}

export const useOrthographicViews = ({
  cameraControlsRef,
  trackballControlsRef,
}: useOrthographicViewsProps) => {
  const {
    data: { gltf },
  } = useCADQuery()
  const { steps } = useOperationSteps()
  const rotating = useRef(false)
  const setState = useCADPageStore(useShallow((state) => state.setState))
  const controlsId = useCADPageStore(useShallow((state) => state.controlsId))
  const target = useCADPageStore(useShallow((state) => state.target))
  const operationStep = useCADPageStore((state) => state.operationStep)
  const hiddenParts = useCADPageStore((state) => state.hiddenParts)
  const hidePreviousOperations = useCADPageStore(
    (state) => state.hidePreviousOperations,
  )
  const showFullModel = useCADPageStore((state) => state.showFullModel)
  const [rotationDirection, setRotationDirection] = useState<Vector3 | null>(
    null,
  )
  const model = useMemo(() => {
    return gltf?.scene.children[0]
  }, [gltf])

  const setRotationTo = useCallback(
    (e: ThreeEvent<MouseEvent>, direction: Vector3) => {
      e.stopPropagation()

      if (rotating.current) return null
      rotating.current = true

      setRotationDirection(direction)
      setState({ controlsId: CONTROLS.CAMERA_CONTROLS, fitting: true })
      return null
    },
    [setState],
  )

  const getFitBoundingBox = useCallback(
    (model: Object3D<Object3DEventMap>) => {
      const fallbackBoundingBox = new Box3().setFromObject(model)

      if (!gltf) return fallbackBoundingBox

      if (!operationStep || showFullModel) {
        return (
          getOperationStepBoundingBox({
            assemblyGroupIds: Array.from(gltf.partNodeMap.keys()),
            gltf,
            hiddenParts,
          }) ?? fallbackBoundingBox
        )
      }

      const activeStep = steps.find((step) => step.id === operationStep.stepId)
      if (!activeStep) return fallbackBoundingBox

      return (
        getOperationStepBoundingBox({
          assemblyGroupIds: getTargetAssemblyGroupIds({
            activeStep,
            hidePreviousOperations,
            steps,
          }),
          gltf,
          hiddenParts,
        }) ?? fallbackBoundingBox
      )
    },
    [
      gltf,
      hiddenParts,
      hidePreviousOperations,
      operationStep,
      showFullModel,
      steps,
    ],
  )

  const rotateAndFitTo = useCallback(
    (model: Object3D<Object3DEventMap>, direction: Vector3) => {
      const boundingBox = getFitBoundingBox(model)
      const rotationTarget = boundingBox.getCenter(new Vector3())
      const viewDirection = direction.clone().normalize()
      const cameraUp = getStableCameraUp(viewDirection)

      if (cameraControlsRef.current && target.length === 3) {
        cameraControlsRef.current.camera.up.copy(cameraUp)
        cameraControlsRef.current.updateCameraUp()
        trackballControlsRef.current?.target.copy(rotationTarget)

        const cameraControls = cameraControlsRef.current

        if (cameraControls) {
          void (async () => {
            try {
              const distance =
                cameraControls.camera.position.distanceTo(rotationTarget)
              const cameraPosition = rotationTarget
                .clone()
                .add(viewDirection.clone().multiplyScalar(distance))

              await cameraControls.setLookAt(
                cameraPosition.x,
                cameraPosition.y,
                cameraPosition.z,
                rotationTarget.x,
                rotationTarget.y,
                rotationTarget.z,
                true,
              )
              await cameraControls.fitToBox(boundingBox, false, {
                cover: false,
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
                paddingTop: 0,
              })
            } catch (error) {
              console.error('Camera operations failed:', error)
            } finally {
              try {
                const targetPreservingPosition = rotationTarget
                  .clone()
                  .add(
                    viewDirection
                      .clone()
                      .multiplyScalar(cameraControls.distance),
                  )

                cameraControls.camera.up.copy(cameraUp)
                cameraControls.updateCameraUp()
                await cameraControls.setLookAt(
                  targetPreservingPosition.x,
                  targetPreservingPosition.y,
                  targetPreservingPosition.z,
                  rotationTarget.x,
                  rotationTarget.y,
                  rotationTarget.z,
                  false,
                )
                cameraControls.update(0)
                cameraControls.camera.updateMatrixWorld()
              } catch (error) {
                console.error('Camera target restore failed:', error)
              }
              trackballControlsRef.current?.target.copy(rotationTarget)

              rotating.current = false
              setRotationDirection(null)
              setState({
                controlsId: CONTROLS.TRACKBALL_CONTROLS,
                fitting: false,
                target: rotationTarget.toArray(),
                cameraPosition: cameraControls.camera.position.toArray(),
                cameraQuaternion: cameraControls.camera.quaternion.toArray(),
                cameraUp: cameraControls.camera.up.toArray(),
              })
            }
          })()
        }
      }
    },
    [
      getFitBoundingBox,
      target,
      setState,
      cameraControlsRef,
      trackballControlsRef,
    ],
  )

  const rotateAndFit = useCallback(() => {
    if (!model || !rotationDirection) return
    rotateAndFitTo(model, rotationDirection)
  }, [model, rotationDirection, rotateAndFitTo])

  useEffect(() => {
    if (controlsId === CONTROLS.CAMERA_CONTROLS && rotating.current) {
      rotateAndFit()
    }
  }, [controlsId, rotateAndFit])

  return { setRotationTo }
}
